import { useEffect, useMemo, useRef } from 'react';
import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import {
  buildPreviewRequestFromPlan,
  planTrialSummary,
  resolveFullCheckoutUpdates,
  resolveSignalManagerCheckout,
  resolveMcpStandaloneCheckout,
  resolveEaHostingCheckout,
  toCheckoutSelection
} from 'lib/billing';
import { addonEntitlementsFromAccount } from './libBilling';
import { useBillingStore } from './useBillingStore';
import { useCheckoutStore } from './useCheckoutStore';
import { useInvoicePreviewStore, type InvoicePreview } from './useInvoicePreviewStore';
import { PAYMENT_TAB } from './types';

/**
 * Owns the debounced PreviewCheckout RPC and writes the result into
 * `useInvoicePreviewStore`. Mount once near the top of the checkout tree
 * (CheckoutProvider). All reads happen via `useInvoicePreview`.
 *
 * Inputs are pulled directly from the source stores (`useCheckoutStore`,
 * `useBillingStore`) instead of being routed through `useCheckoutState`'s
 * derived compat blob — keeps this effect's dependency set minimal so it
 * doesn't re-run on unrelated state changes.
 */
export function useInvoicePreviewSync(): void {
  const { isAuthenticated, user } = useAuth() as any;
  const getBillingClient = useBillingClient();

  const selection = useCheckoutStore((s) => s.selection);
  const drawerOpen = useCheckoutStore((s) => s.drawerOpen);
  const coupon = useCheckoutStore((s) => s.coupon);
  const setCoupon = useCheckoutStore((s) => s.setCoupon);
  // The crypto tab is quoted as crypto pays: without a waiting cancel offer, and added lines as their own plans.
  const paymentTab = useCheckoutStore((s) => s.paymentTab);
  const offerWaiting = useInvoicePreviewStore((s) => Boolean(s.preview?.cancelOfferPercentBps));
  const subscriptions = useBillingStore((s) => s.subscriptions);
  const catalog = useBillingStore((s) => s.catalog);
  const subscriptionsLoading = useBillingStore((s) => s.loading);
  const catalogLoading = useBillingStore((s) => s.catalogLoading);
  const overview = useBillingStore((s) => s.overview);

  const setPreview = useInvoicePreviewStore((s) => s.setPreview);
  const setLoading = useInvoicePreviewStore((s) => s.setLoading);
  const setError = useInvoicePreviewStore((s) => s.setError);
  const setHasPurchaseOps = useInvoicePreviewStore((s) => s.setHasPurchaseOps);
  const reset = useInvoicePreviewStore((s) => s.reset);

  const authCheckout = Boolean(isAuthenticated);

  // The client factory changes identity whenever auth re-renders (a token refresh); read the latest
  // one at send time instead of re-quoting for it.
  const getClientRef = useRef(getBillingClient);
  getClientRef.current = getBillingClient;
  // The request last sent or answered. A re-run that builds the same request keeps its quote, so
  // re-renders and billing refetches with unchanged content never reload the summary.
  const requestKeyRef = useRef<string | null>(null);

  const currentAddons = useMemo(
    () => addonEntitlementsFromAccount((overview as any)?.addOns, subscriptions, catalog),
    [overview, subscriptions, catalog]
  );

  useEffect(() => {
    const clear = () => {
      requestKeyRef.current = null;
      reset();
    };
    if (!drawerOpen || !authCheckout) {
      clear();
      return;
    }
    // A billing refetch in flight: keep the current quote until the new data lands.
    if (subscriptionsLoading || catalogLoading) return;
    if (!catalog) {
      clear();
      return;
    }

    const sel = selection as any;
    let plan: any;
    let useTrial = false;
    if (sel.signalManagerSelected) {
      // Standalone Signal Manager plan — create-only, no trial (mirrors
      // PaymentStep.executeCheckout).
      plan = resolveSignalManagerCheckout(catalog);
    } else if (sel.mcpStandalone) {
      // Standalone AI Connect plan — must mirror PaymentStep.executeCheckout
      // exactly or the quote diverges from the charge. Create-only, which is
      // also a purchase op, so the coupon rides the preview (hasPurchaseOps
      // below).
      plan = resolveMcpStandaloneCheckout(catalog, sel.billingCycle);
    } else if (sel.eaHostingStandalone) {
      // Standalone EA Hosting plan — same mirror requirement as MCP above.
      // Resolves to a create for a new buyer or a slot-quantity modify on the
      // owned EA sub. A new buyer's create trials when the price carries
      // trial_period_days and the EA trial is unused (checkoutPlan); a modify
      // never does. No "Buy now" choice here, so never read sel.skipTrial.
      plan = resolveEaHostingCheckout(catalog, sel.billingCycle, sel.eaHostingSlots, subscriptions || []);
      const trialUsedProductIds = ((overview as any)?.trialUsedProductIds as string[]) || [];
      useTrial = planTrialSummary(plan, catalog, trialUsedProductIds, false).applies;
    } else {
      // Carries mcpEnabled + dedicatedKinds so the preview matches PaymentStep's charge.
      const previewSelection = toCheckoutSelection(sel);

      const trialUsedProductIds = ((overview as any)?.trialUsedProductIds as string[]) || [];
      plan = resolveFullCheckoutUpdates(previewSelection, catalog, subscriptions || [], currentAddons, {
        trialUsedProductIds,
        skipTrial: Boolean(sel.skipTrial)
      });
      // Trial is per new-sub: derive it from the plan's `creates` buckets, not the
      // whole cart, so the preview $0 matches what each created sub is actually
      // granted (an owned/existing line in the cart can't veto it). `applies`
      // already factors the buyer's "Buy now" choice and the one-time used set.
      useTrial = planTrialSummary(plan, catalog, trialUsedProductIds, Boolean(sel.skipTrial)).applies;
      // Variant swap (e.g. futures default → unlimited) on an existing sub must
      // never re-apply trial. Stripe inherits `trial_period_days` from the new
      // price when an item is added/updated on a sub — even if the sub itself
      // is past trial. Force useTrial=false whenever the plan contains a price
      // swap so BM/Stripe bill the prorated delta immediately instead of $0.
      const hasPriceSwap = (plan as any)?.ops?.some?.((op: any) => (op.modifies || []).some((m: any) => Boolean(m.priceId)));
      if (hasPriceSwap) useTrial = false;
    }

    if (!plan) {
      clear();
      return;
    }

    // A discount code rides every purchase op: creates apply it at
    // subscription level, adds/modifies attach it at item level to the changed
    // line (billing-manager scopes it server-side, and its anchor preview
    // attaches the same item-level discounts — quote matches charge). Only a
    // plan with no purchase ops at all (e.g. removals only) has nothing for a
    // code to ride. Recomputed on every cart change (`selection` is in the
    // dep list), so emptying the cart after applying a code flips the flag
    // and the note below immediately.
    const hasPurchaseOps =
      (plan as any)?.type === 'multi-cycle' &&
      Boolean(
        (plan as any)?.ops?.some?.(
          (op: any) => (op.creates || []).length > 0 || (op.adds || []).length > 0 || (op.modifies || []).length > 0
        )
      );
    setHasPurchaseOps(hasPurchaseOps);

    // Store-held discount code — the preview re-quotes whenever it changes
    // (`coupon` is in this effect's dep list), so `preview.discount` reflects
    // the typed code before the buyer ever reaches Payment. Captured here and
    // published with the reply so consumers can tell which code (if any) the
    // settled quote was priced with.
    const requestCoupon = hasPurchaseOps ? coupon : '';
    const cryptoQuote = paymentTab === PAYMENT_TAB.CRYPTO;
    const skipCancelOffer = offerWaiting && cryptoQuote;
    const built = buildPreviewRequestFromPlan(plan, catalog, {
      userId: user?.tcId || '',
      coupon: requestCoupon,
      useTrial,
      addsAsNewSubs: cryptoQuote
    });
    const body = built && { ...built, skipCancelOffer };

    if (!body) {
      clear();
      return;
    }

    const key = JSON.stringify(body);
    if (key === requestKeyRef.current) return;
    requestKeyRef.current = key;
    // Only the newest request may write; an older answer arriving late is dropped.
    const isCurrent = () => requestKeyRef.current === key;
    let sent = false;
    const timer = setTimeout(async () => {
      sent = true;
      setLoading(true);
      setError(null);
      try {
        const client = await getClientRef.current();
        const resp = (await client.previewCheckout(body as any)) as InvoicePreview;
        if (isCurrent()) {
          setPreview(resp, requestCoupon, cryptoQuote);
          // A waiting cancel offer stands in for a typed code, which then re-quotes away.
          if (resp?.cancelOfferPercentBps && requestCoupon) setCoupon('');
        }
      } catch (err) {
        if (isCurrent()) {
          // Forget the failed request, so the next run asks again.
          requestKeyRef.current = null;
          setPreview(null, '');
          setError(err);
        }
      } finally {
        if (isCurrent() || requestKeyRef.current === null) setLoading(false);
      }
    }, 350);

    return () => {
      // A request still waiting on its debounce is withdrawn; one already sent finishes and is kept
      // if it is still the newest.
      if (sent) return;
      clearTimeout(timer);
      if (isCurrent()) requestKeyRef.current = null;
    };
  }, [
    drawerOpen,
    authCheckout,
    subscriptionsLoading,
    catalogLoading,
    selection,
    coupon,
    paymentTab,
    offerWaiting,
    catalog,
    subscriptions,
    currentAddons,
    overview,
    user?.tcId,
    setPreview,
    setLoading,
    setError,
    setHasPurchaseOps,
    setCoupon,
    reset
  ]);
}
